import { cabinetSvg } from '../drawing/cabinet';
import { doorGroup, type DoorLook, type DoorStyle } from '../drawing/door';
import { esc, idSource, n, safeHex, shade, type IdSource } from '../drawing/svg';
import { bayGeometry, frontsFor, insideHeight, limitsKey, RULES, slopeAt, typeName, type Cabinet } from './model';

/**
 * The cabinet builder's pictures, as pure string SVG (see `drawing/svg.ts`):
 * <ul>
 *   <li>{@link cabinetPreview}: the cabinet as it will look, the mockup's
 *       renderers (`cornerL`, `cornerBlind`, `cornerDiag`, `stairs`) ported;</li>
 *   <li>{@link measuredDrawing}: a front elevation with every dimension the
 *       order is made to: overall sizes, each front, shelf heights (B8.3);</li>
 *   <li>{@link footprintSvg}: the small "from above" plan.</li>
 * </ul>
 */
export interface Look {
  style: DoorStyle;
  /** The door range's own look, for its frame and moulding. */
  doorLook?: DoorLook;
  colour: string;
  wood?: boolean;
  carcassHex: string;
  idPrefix?: string;
}

const PLINTH_DRAWN = 100;

function wrap(vbW: number, vbH: number, body: string, label: string, pad = 40): string {
  return `<svg viewBox="${n(-pad)} ${n(-pad)} ${n(vbW + 2 * pad)} ${n(vbH + 2 * pad)}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(label)}">${body}</svg>`;
}

function openBay(x: number, y: number, w: number, h: number, shelves: number[], inside: number, carcass: string): string {
  let g = `<rect x="${n(x + 18)}" y="${n(y + 18)}" width="${n(w - 36)}" height="${n(h - 36)}" fill="${shade(carcass, -0.07)}"/>`;
  for (const s of shelves) {
    // Shelf positions are measured from the inside bottom; draw them from the top.
    const yy = y + 18 + (h - 36) * (1 - s / Math.max(1, inside));
    g += `<rect x="${n(x + 18)}" y="${n(yy - 8)}" width="${n(w - 36)}" height="16" fill="${shade(carcass, 0.25)}" stroke="${shade(carcass, -0.2)}"/>`;
  }
  return g;
}

function cornerL(c: Cabinet, look: Look, ids: IdSource, label: string): string {
  const carcass = safeHex(look.carcassHex, '#f1ede6');
  const hex = safeHex(look.colour);
  const H = c.h;
  const fw = Math.max(250, c.w - c.d);
  const rw = Math.max(250, c.b - c.d);
  const k = 0.55;
  const lift = rw * k * 0.5;
  const inside = insideHeight(c);
  let g = `<g transform="translate(0,${n(lift)})">`;
  g += `<polygon points="0,0 ${n(fw)},0 ${n(fw + rw * k)},${n(-lift)} ${n(rw * k * 0.35)},${n(-lift - 30)}" fill="${shade(carcass, 0.35)}" stroke="${shade(carcass, -0.2)}"/>`;
  g += `<rect width="${n(fw)}" height="${n(H)}" fill="${carcass}" stroke="${shade(carcass, -0.25)}" stroke-width="2"/>`;
  g += `<g transform="matrix(${k},${n(-k * 0.5)},0,1,${n(fw)},0)"><rect width="${n(rw)}" height="${n(H)}" fill="${shade(carcass, -0.1)}" stroke="${shade(carcass, -0.3)}" stroke-width="2"/>`;
  g += c.fronts ? doorGroup(ids, 3, 3, rw - 6, H - 6, look.style, hex, { look: look.doorLook, wood: look.wood, hinge: 'right' }) : openBay(0, 0, rw, H, c.shelves, inside, carcass);
  g += '</g>';
  g += c.fronts ? doorGroup(ids, 3, 3, fw - 6, H - 6, look.style, hex, { look: look.doorLook, wood: look.wood, hinge: 'left' }) : openBay(0, 0, fw, H, c.shelves, inside, carcass);
  g += `<rect x="30" y="${n(H)}" width="${n(fw - 30)}" height="${PLINTH_DRAWN}" fill="${shade(hex, -0.25)}"/>`;
  return wrap(fw + rw * k, H + PLINTH_DRAWN + lift, `${g}</g>`, label);
}

function cornerBlind(c: Cabinet, look: Look, ids: IdSource, label: string): string {
  const carcass = safeHex(look.carcassHex, '#f1ede6');
  const hex = safeHex(look.colour);
  const W = c.w;
  const H = c.h;
  const bw = c.blank ?? 500;
  const dw = W - bw;
  const bx = c.blind === 'left' ? 0 : dw;
  const dx = c.blind === 'left' ? bw : 0;
  const clip = ids.next();
  let g = `<rect width="${n(W)}" height="${n(H)}" fill="${carcass}" stroke="${shade(carcass, -0.25)}" stroke-width="2"/>`;
  g += `<defs><clipPath id="${clip}"><rect x="${n(bx + 3)}" y="3" width="${n(bw - 6)}" height="${n(H - 6)}"/></clipPath></defs>`;
  g += `<rect x="${n(bx + 3)}" y="3" width="${n(bw - 6)}" height="${n(H - 6)}" fill="${shade(carcass, -0.12)}"/>`;
  g += `<g clip-path="url(#${clip})">`;
  for (let i = 0; i < 14; i++) {
    g += `<line x1="${n(bx + (i * bw) / 10)}" y1="0" x2="${n(bx + (i * bw) / 10 - 120)}" y2="${n(H)}" stroke="${shade(carcass, -0.22)}" stroke-width="3" opacity=".6"/>`;
  }
  g += '</g>';
  g += `<text x="${n(bx + bw / 2)}" y="${n(H / 2)}" text-anchor="middle" font-family="Manrope,sans-serif" font-weight="800" font-size="34" fill="${shade(carcass, -0.5)}">blind section</text>`;
  g += `<text x="${n(bx + bw / 2)}" y="${n(H / 2 + 40)}" text-anchor="middle" font-family="Manrope,sans-serif" font-size="26" fill="${shade(carcass, -0.45)}">hidden under the return run</text>`;
  if (c.fronts) {
    // As frontsFor: two doors once one would be wider than a single door may be.
    const count = dw - RULES.frontWidthLess > RULES.maxSingleDoor ? 2 : 1;
    const each = (dw - 6 - RULES.frontGap * (count - 1)) / count;
    for (let i = 0; i < count; i++) {
      const hinge = count === 2 ? (i === 0 ? 'left' : 'right') : c.blind === 'left' ? 'right' : 'left';
      g += doorGroup(ids, dx + 3 + i * (each + RULES.frontGap), 3, each, H - 6, look.style, hex, { look: look.doorLook, wood: look.wood, hinge });
    }
  } else {
    g += openBay(dx, 0, dw, H, c.shelves, insideHeight(c), carcass);
  }
  g += `<rect x="30" y="${n(H)}" width="${n(W - 30)}" height="${PLINTH_DRAWN}" fill="${shade(hex, -0.25)}"/>`;
  return wrap(W, H + PLINTH_DRAWN, g, label);
}

function cornerDiag(c: Cabinet, look: Look, ids: IdSource, label: string): string {
  const carcass = safeHex(look.carcassHex, '#f1ede6');
  const hex = safeHex(look.colour);
  const H = c.h;
  const face = Math.round((c.w - c.d) * Math.SQRT2);
  const side = c.d * 0.9;
  const rise = side * 0.35;
  let g = `<g transform="translate(${n(side)},${n(rise * 2.2)})">`;
  g += `<polygon points="${n(-side)},${n(-rise)} 0,0 0,${n(H)} ${n(-side)},${n(H - rise)}" fill="${shade(carcass, -0.14)}" stroke="${shade(carcass, -0.3)}"/>`;
  g += `<polygon points="${n(face + side)},${n(-rise)} ${n(face)},0 ${n(face)},${n(H)} ${n(face + side)},${n(H - rise)}" fill="${shade(carcass, -0.08)}" stroke="${shade(carcass, -0.3)}"/>`;
  g += `<polygon points="${n(-side)},${n(-rise)} 0,0 ${n(face)},0 ${n(face + side)},${n(-rise)} ${n(face / 2)},${n(-rise * 2.2)}" fill="${shade(carcass, 0.35)}" stroke="${shade(carcass, -0.2)}"/>`;
  g += `<rect width="${n(face)}" height="${n(H)}" fill="${carcass}" stroke="${shade(carcass, -0.25)}" stroke-width="2"/>`;
  g += c.fronts
    ? doorGroup(ids, 3, 3, face - 6, H - 6, look.style, hex, { look: look.doorLook, wood: look.wood, hinge: c.hinge, handleAt: 'bottom' })
    : openBay(0, 0, face, H, c.shelves, insideHeight(c), carcass);
  return wrap(face + 2 * side, H + rise * 2.2, `${g}</g>`, label);
}

function stairs(c: Cabinet, look: Look, label: string): string {
  const carcass = safeHex(look.carcassHex, '#f1ede6');
  const hex = safeHex(look.colour);
  const W = c.w;
  const H = c.h;
  const y = (x: number) => H - slopeAt(x, c);
  const gap = 5;
  const plinth = 60;
  const framed = !(look.style === 'slab' || look.style === 'jpull');
  const front = (xa: number, xb: number, top: (x: number) => number, yb: number, handle: 'cup' | 'left' | 'right') => {
    let f = `<polygon points="${n(xa)},${n(top(xa))} ${n(xb)},${n(top(xb))} ${n(xb)},${n(yb)} ${n(xa)},${n(yb)}" fill="${hex}" stroke="${shade(hex, -0.32)}" stroke-width="3"/>`;
    const inset = Math.min(26, (xb - xa) / 6, (yb - top(xa)) / 5);
    if (framed && inset > 8) {
      f += `<polygon points="${n(xa + inset)},${n(top(xa) + inset * 1.2)} ${n(xb - inset)},${n(top(xb) + inset * 1.2)} ${n(xb - inset)},${n(yb - inset)} ${n(xa + inset)},${n(yb - inset)}" fill="none" stroke="${shade(hex, -0.18)}" stroke-width="4"/>`;
    }
    if (handle === 'cup') {
      f += `<rect x="${n((xa + xb) / 2 - 40)}" y="${n(Math.max(top((xa + xb) / 2), top(xb)) + 26)}" width="80" height="12" rx="6" fill="#b08d57"/>`;
    } else {
      const hx = handle === 'right' ? xb - 30 : xa + 22;
      f += `<rect x="${n(hx)}" y="${n(top(hx) + 60)}" width="10" height="120" rx="5" fill="#b08d57"/>`;
    }
    return f;
  };
  let g = `<polygon points="0,${n(y(0))} ${n(W)},${n(y(W))} ${n(W)},${n(H)} 0,${n(H)}" fill="${carcass}" stroke="${shade(carcass, -0.3)}" stroke-width="3"/>`;
  const steps = 8;
  for (let i = 0; i < steps; i++) {
    const x0 = (W / steps) * i;
    const x1 = (W / steps) * (i + 1);
    g += `<path d="M${n(x0)} ${n(y(x0) - 26)} H${n(x1)} V${n(y(x1) - 26)}" stroke="#b9a584" stroke-width="10" fill="none"/>`;
  }
  for (const b of bayGeometry(c)) {
    if (b.x1 <= b.x0) continue;
    const xa = b.x0 + gap / 2;
    const xb = b.x1 - gap / 2;
    const yTop = (x: number) => y(x) + 4;
    const yb = H - 3;
    if (!c.fronts || b.kind === 'open') {
      g += `<polygon points="${n(xa)},${n(yTop(xa))} ${n(xb)},${n(yTop(xb))} ${n(xb)},${n(yb)} ${n(xa)},${n(yb)}" fill="${shade(carcass, -0.08)}" stroke="${shade(carcass, -0.3)}" stroke-width="3"/>`;
      const levels = b.kind === 'drawers' ? (b.n ?? 1) : b.kind === 'open' ? 2 : 1;
      for (let i = 1; i <= levels; i++) {
        const ly = H - (b.lo / (levels + 1)) * i;
        g += `<line x1="${n(xa + 10)}" y1="${n(ly)}" x2="${n(xb - 10)}" y2="${n(ly)}" stroke="${shade(carcass, -0.35)}" stroke-width="7"/>`;
      }
      continue;
    }
    if (b.kind === 'pullout') g += front(xa, xb, yTop, yb, 'cup');
    if (b.kind === 'door') g += front(xa, xb, yTop, yb, 'right');
    if (b.kind === 'double') {
      const xm = (xa + xb) / 2;
      g += front(xa, xm - gap / 2, yTop, yb, 'right') + front(xm + gap / 2, xb, yTop, yb, 'left');
    }
    if (b.kind === 'drawers') {
      const count = Math.max(1, b.n ?? 1);
      const band = (b.lo - 10) / count;
      for (let i = 0; i < count; i++) {
        const bottom = H - 3 - i * band;
        const top = bottom - band + gap;
        g += front(xa, xb, i === count - 1 ? yTop : () => top, bottom, 'cup');
      }
    }
  }
  g += `<rect x="0" y="${n(H)}" width="${n(W)}" height="${plinth}" fill="${shade(hex, -0.3)}"/>`;
  g += `<text x="${n(W - 10)}" y="${n(y(W) - 60)}" text-anchor="end" font-family="Manrope,sans-serif" font-weight="700" font-size="${n(Math.max(30, W / 45))}" fill="#8a7658">underside of stairs</text>`;
  return wrap(W, H + plinth, g, label, 90);
}

/** The cabinet as it will look, in the chosen door style and colour. */
export function cabinetPreview(c: Cabinet, look: Look): string {
  const ids = idSource(look.idPrefix ?? 'cabp');
  const label = `${typeName(c)}, ${c.w} wide by ${c.h} high${c.fronts ? '' : ', carcass only'}`;
  switch (limitsKey(c.type, c.corner)) {
    case 'corner-l':
      return cornerL(c, look, ids, label);
    case 'corner-blind':
      return cornerBlind(c, look, ids, label);
    case 'corner-diag':
      return cornerDiag(c, look, ids, label);
    case 'stairs':
      return stairs(c, look, label);
    default:
      return cabinetSvg({
        type: c.type === 'wardrobe' ? 'tall' : (c.type as 'base' | 'wall' | 'tall' | 'drawers'),
        w: c.w,
        h: c.h,
        d: c.d,
        doors: c.doors || undefined,
        upper: c.type === 'tall' ? c.upper : undefined,
        drawers: c.drawerFronts.length,
        open: !c.fronts,
        shelves: c.shelves.length,
        style: look.style,
        look: look.doorLook,
        colour: look.colour,
        wood: look.wood,
        carcass: look.carcassHex,
        label,
        idPrefix: look.idPrefix,
      });
  }
}

// ── Measured drawing ───────────────────────────────────────────────────────

const DIM = '#3a5a8c';
const FONT = 'font-family="Manrope,sans-serif" font-weight="700"';

function hDim(x0: number, x1: number, y: number, text: string, size: number): string {
  return (
    `<line x1="${n(x0)}" y1="${n(y)}" x2="${n(x1)}" y2="${n(y)}" stroke="${DIM}" stroke-width="${n(size / 10)}"/>` +
    `<line x1="${n(x0)}" y1="${n(y - size / 2)}" x2="${n(x0)}" y2="${n(y + size / 2)}" stroke="${DIM}" stroke-width="${n(size / 10)}"/>` +
    `<line x1="${n(x1)}" y1="${n(y - size / 2)}" x2="${n(x1)}" y2="${n(y + size / 2)}" stroke="${DIM}" stroke-width="${n(size / 10)}"/>` +
    `<text x="${n((x0 + x1) / 2)}" y="${n(y - size * 0.4)}" text-anchor="middle" ${FONT} font-size="${n(size)}" fill="${DIM}">${esc(text)}</text>`
  );
}

function vDim(x: number, y0: number, y1: number, text: string, size: number): string {
  const cy = (y0 + y1) / 2;
  return (
    `<line x1="${n(x)}" y1="${n(y0)}" x2="${n(x)}" y2="${n(y1)}" stroke="${DIM}" stroke-width="${n(size / 10)}"/>` +
    `<line x1="${n(x - size / 2)}" y1="${n(y0)}" x2="${n(x + size / 2)}" y2="${n(y0)}" stroke="${DIM}" stroke-width="${n(size / 10)}"/>` +
    `<line x1="${n(x - size / 2)}" y1="${n(y1)}" x2="${n(x + size / 2)}" y2="${n(y1)}" stroke="${DIM}" stroke-width="${n(size / 10)}"/>` +
    `<text x="${n(x - size * 0.4)}" y="${n(cy)}" text-anchor="middle" ${FONT} font-size="${n(size)}" fill="${DIM}" transform="rotate(-90 ${n(x - size * 0.4)} ${n(cy)})">${esc(text)}</text>`
  );
}

/**
 * A front elevation to the millimetre: overall width and height, the plinth,
 * each front's size, and every shelf's height from the inside bottom. An L
 * corner is drawn from above instead, since its sizes are on the floor.
 */
export function measuredDrawing(c: Cabinet): string {
  const key = limitsKey(c.type, c.corner);
  const floor = c.type !== 'wall' && key !== 'corner-diag';
  const size = Math.max(24, Math.min(c.w, c.h) / 16);
  const label = `Measured drawing: ${typeName(c)}`;
  const line = 'fill="#fffdf9" stroke="#1d211f" stroke-width="3"';

  if (key === 'corner-l') {
    const A = c.w;
    const B = c.b;
    const D = c.d;
    let g = `<path d="M0 0 H${n(A)} V${n(D)} H${n(D)} V${n(B)} H0 Z" ${line}/>`;
    g += hDim(0, A, -size * 1.4, `${A} mm`, size);
    g += vDim(-size * 1.4, 0, B, `${B} mm`, size);
    g += vDim(A + size * 1.6, 0, D, `${D} deep`, size);
    g += `<text x="${n(A / 2)}" y="${n(D / 2 + size / 3)}" text-anchor="middle" ${FONT} font-size="${n(size)}" fill="#646a65">from above · ${c.h} mm high</text>`;
    return wrap(A, B, g, label, size * 3.5);
  }

  const W = key === 'corner-diag' ? Math.round((c.w - c.d) * Math.SQRT2) : c.w;
  const H = c.h;
  const plinth = floor ? RULES.plinth : 0;
  let g = '';
  if (key === 'stairs') {
    g += `<polygon points="0,0 ${n(W)},${n(H - c.h2)} ${n(W)},${n(H)} 0,${n(H)}" ${line}/>`;
    for (const b of bayGeometry(c)) {
      if (b.x0 > 0) g += `<line x1="${n(b.x0)}" y1="${n(H - b.hi)}" x2="${n(b.x0)}" y2="${n(H)}" stroke="#1d211f" stroke-width="2"/>`;
      g += hDim(b.x0, Math.min(W, b.x0 + b.w), H + plinth + size * 1.6, `${b.w}`, size * 0.8);
    }
    g += vDim(W + size * 1.6, H - c.h2, H, `${c.h2} mm`, size);
  } else {
    g += `<rect width="${n(W)}" height="${n(H)}" ${line}/>`;
  }
  if (plinth) g += `<rect x="40" y="${n(H)}" width="${n(W - 80)}" height="${plinth}" fill="#e2d9cc" stroke="#857a6b" stroke-width="2"/>`;

  // Fronts, laid over the carcass, each labelled with its size.
  const fronts = frontsFor(c);
  if (key === 'drawers') {
    let yy = 2;
    fronts.forEach((f) => {
      g += `<rect x="${n(1.5)}" y="${n(yy)}" width="${n(f.w)}" height="${n(f.h)}" fill="none" stroke="#2e4a3e" stroke-width="3" stroke-dasharray="12 6"/>`;
      g += `<text x="${n(W / 2)}" y="${n(yy + f.h / 2 + size / 3)}" text-anchor="middle" ${FONT} font-size="${n(size * 0.8)}" fill="#2e4a3e">${f.h} × ${f.w}</text>`;
      yy += f.h + RULES.frontGap;
    });
  } else if (key !== 'stairs' && key !== 'corner-diag' && key !== 'corner-blind' && fronts.length) {
    let xx = 1.5;
    fronts.forEach((f) => {
      for (let k = 0; k < f.qty; k++) {
        g += `<rect x="${n(xx)}" y="2" width="${n(f.w)}" height="${n(f.h)}" fill="none" stroke="#2e4a3e" stroke-width="3" stroke-dasharray="12 6"/>`;
        g += `<text x="${n(xx + f.w / 2)}" y="${n(size * 1.6)}" text-anchor="middle" ${FONT} font-size="${n(size * 0.8)}" fill="#2e4a3e">${f.h} × ${f.w}</text>`;
        xx += f.w + RULES.frontGap;
      }
    });
  }

  // Shelves: dashed, with their height from the inside bottom.
  if (key !== 'stairs' && key !== 'drawers') {
    for (const s of c.shelves) {
      const yy = H - RULES.board - s;
      g += `<line x1="${RULES.board}" y1="${n(yy)}" x2="${n(W - RULES.board)}" y2="${n(yy)}" stroke="#a4573a" stroke-width="3" stroke-dasharray="8 6"/>`;
      g += `<text x="${n(W - RULES.board - 8)}" y="${n(yy - 8)}" text-anchor="end" ${FONT} font-size="${n(size * 0.7)}" fill="#a4573a">shelf ${s} mm</text>`;
    }
  }

  g += hDim(0, W, -size * 1.4, key === 'corner-diag' ? `${W} mm face (${c.w} × ${c.w} on the walls)` : `${W} mm`, size);
  g += vDim(-size * 1.4, 0, H, `${H} mm`, size);
  if (plinth) g += vDim(-size * 1.4, H, H + plinth, `${plinth}`, size * 0.8);
  g += `<text x="${n(W)}" y="${n(H + plinth + size * (key === 'stairs' ? 3.6 : 1.6))}" text-anchor="end" ${FONT} font-size="${n(size * 0.8)}" fill="#646a65">${c.d} mm deep</text>`;
  return wrap(W, H + plinth + (key === 'stairs' ? size * 3 : 0), g, label, size * 3.5);
}

/** The small "from above" plan beside the preview. */
export function footprintSvg(c: Cabinet, idPrefix = 'fp'): string {
  const key = limitsKey(c.type, c.corner);
  const hatch = idSource(idPrefix).next();
  let p: string;
  if (key === 'corner-l') p = '<path d="M10 10h80v30H40v50H10z" fill="#8a9a82"/>';
  else if (key === 'corner-blind') {
    const left = c.blind === 'left';
    p = `<rect x="10" y="30" width="80" height="30" fill="#8a9a82"/><rect x="${left ? 10 : 50}" y="30" width="40" height="30" fill="url(#${hatch})"/><rect x="${left ? 10 : 60}" y="0" width="30" height="30" fill="#d3c7b6"/>`;
  } else if (key === 'corner-diag') p = '<path d="M10 10h40v14L24 50H10z" fill="#8a9a82"/><path d="M50 10h40M10 50v40" stroke="#d3c7b6" stroke-width="14"/>';
  else if (key === 'stairs') p = `<rect x="10" y="30" width="80" height="34" fill="#8a9a82"/>${[0, 1, 2].map((i) => `<rect x="${12 + i * 26}" y="68" width="22" height="${10 + i * 5}" fill="#8a9a82" opacity=".45"/>`).join('')}`;
  else p = '<rect x="20" y="30" width="60" height="34" fill="#8a9a82"/>';
  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Seen from above"><defs><pattern id="${hatch}" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="6" height="6" fill="#cdbfa8"/><line x1="0" y1="0" x2="0" y2="6" stroke="#8a7658" stroke-width="2"/></pattern></defs>${p}</svg>`;
}
